import { FCM_TOKENS } from '@/config/api/api-routes';
import { useUIStore } from '@/config/stores/ui-store';
import { deleteFcmToken } from '@/core/notification';
import { getDeviceId } from '@/shared/helpers/get-device-id';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from '@/shared/utilities/local-storage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { JwtTokenPayload } from '../models/jwt-token-payload';
import { authRefreshToken, getCurrentUser } from '../services/auth.service';

/**
 * Auth Store - Quản lý authentication state
 * Chỉ chứa: user info, tokens, và auth status
 */

interface AuthState {
  user: Nullable<JwtTokenPayload>;
  isAuthenticated: boolean;
  refreshingPromise?: Promise<string | undefined>;
}

interface AuthActions {
  setAuth: (data: {
    user: Partial<JwtTokenPayload>;
    accessToken: string;
    refreshToken: string;
    expiredAt: string;
  }) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<JwtTokenPayload>) => void;
  refreshAccessToken: () => Promise<string | undefined>;
  getValidToken: () => Promise<string | undefined>;
  logout: () => Promise<void>;
  load: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      refreshingPromise: undefined,

      // Actions
      setAuth: (data) => {
        setLocalStorage(data.accessToken, data.refreshToken);
        set({
          user: data.user as any,
          isAuthenticated: true
        });
      },

      clearAuth: () => {
        import.meta.env.VITE_USE_LEGACY !== 'true' && removeLocalStorage();
        set({
          user: null,
          isAuthenticated: false
        });
      },

      updateUser: (userData) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null
        }));
      },

      refreshAccessToken: async () => {
        if (get().refreshingPromise) return get().refreshingPromise;
        const promise = (async () => {
          const { accessToken, refreshToken } = getLocalStorage();
          if (!refreshToken || !accessToken) {
            import.meta.env.VITE_USE_LEGACY === 'true' && get().clearAuth();
            return undefined;
          }

          try {
            console.info('[Auth] Refreshing token');
            const res = await authRefreshToken(accessToken || '', refreshToken);

            setLocalStorage(res.accessToken, res.refreshToken);
            set({ isAuthenticated: true });

            return res.accessToken;
          } catch (error) {
            console.error('[Auth] Token refresh failed:', error);
            get().clearAuth();
            return undefined;
          }
        })();

        set({ refreshingPromise: promise });

        try {
          return await promise;
        } finally {
          set({ refreshingPromise: undefined });
        }
      },

      /**
       * Helper: Lấy token hợp lệ, tự động refresh nếu cần
       */
      getValidToken: async () => {
        const { accessToken, refreshToken } = getLocalStorage();
        if (!accessToken || !refreshToken) return undefined;
        return accessToken;
      },

      logout: async () => {
        const { accessToken } = getLocalStorage();
        const deviceId = getDeviceId();
        try {
          if (accessToken) {
            deleteFcmToken();
            const url = `${import.meta.env.VITE_API_URL}${FCM_TOKENS}/device/${deviceId}`;
            await fetch(url, {
              method: 'DELETE',
              headers: {
                Accept: '*/*',
                Authorization: `Bearer ${accessToken}`
              }
            });
          }
        } catch (error) {
          console.error('[FCM] Unregister device token failed', error);
        } finally {
          get().clearAuth();
        }
      },

      load: async () => {
        const { accessToken } = getLocalStorage();
        const userMe = await getCurrentUser(accessToken || '');

        if (userMe.id !== get().user?.user_id) {
          console.info('[Auth] User info updated, updating store');
          useUIStore.getState().reset();
        }

        set({
          isAuthenticated: Boolean(userMe),
          user: { user_id: userMe.id } as any
        });
      }
    }),
    {
      name: 'chat-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);
